// pozycje zamówinenia - lokalna wersja z kolumną exportu
// @ts-nocheck

import React, { FC, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import classnames from 'classnames';

import { useRWD } from 'hooks';
import { useGetOrderExport, useGetOrderPositions, useGetPanelOrderPositions } from 'api';
import { IOrderPositionListItem } from 'api/types';
import { AddPositionsToCartSelect } from 'components/containers';
import { Link, Button, Checkbox } from 'components/controls';
import Table, { IColumn } from 'components/controls/Table';

import { ArrowDropdown, FileExportIcon } from 'assets/icons';
import styles from 'theme/pages/Order/components/OrderPositions/OrderPositions.module.scss';
import ProductMobile from 'components/containers/ProductMobile';
import serviceIcon from 'assets/images/service-circle.png';

const isOrderPositionService = (item: IOrderPositionListItem) => item.product_id < 0;

// typ danych wejściowych
interface IProps {
  orderId: number;
  isPanel: boolean;
}

const OrderPositions: FC<IProps> = ({ orderId, isPanel }) => {
  const { isMobile } = useRWD();

  // zaznaczone pozycje
  const [checkedItemIds, setCheckedItemIds] = useState<number[]>([]);

  // export zamówienia do excel
  const { refetch: downloadExcel } = useGetOrderExport(
    orderId || 0,
    { export_type: 'xls' },
    {
      enabled: false,
      onSuccess: (data) => {
        const a = document.createElement('a');
        a.download = data.file_name;
        a.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.content}`;
        a.click();
      }
    }
  );

  // pobranie listy pozycji
  const { data: orderPositionsData } = (isPanel ? useGetPanelOrderPositions : useGetOrderPositions)(
    orderId,
    {
      page: 1,
      limit: 999
    }
  );

  const columns: IColumn<IOrderPositionListItem>[] = useMemo(
    () => [
      {
        title: (
          <>
            <Checkbox
              checked={
                checkedItemIds.length > 0 &&
                checkedItemIds.length === (orderPositionsData?.items || []).length
              }
              indeterminate={
                checkedItemIds.length > 0 &&
                checkedItemIds.length < (orderPositionsData?.items || []).length
              }
              onClick={(event) => {
                event.stopPropagation();
                setCheckedItemIds(
                  checkedItemIds.length < (orderPositionsData?.items || []).length
                    ? (orderPositionsData?.items || []).map((position) => position.id)
                    : []
                );
              }}
            />
            <div className={styles.counter}>
              <Trans>LP</Trans>
            </div>
          </>
        ),
        key: 'lp',
        align: 'center',
        renderCell: (item, index) => {
          const isChecked = checkedItemIds.includes(item.id);
          const isService = isOrderPositionService(item);

          return (
            <>
              {!isService && (
                <Checkbox
                  checked={isChecked}
                  onClick={() => {
                    setCheckedItemIds((old) =>
                      isChecked ? old.filter((checkbox) => checkbox !== item.id) : [...old, item.id]
                    );
                  }}
                />
              )}
              <div className={styles.counter}>{index + 1}</div>
            </>
          );
        },
        width: 100
      },
      {
        title: '',
        key: 'product-image',
        renderCell: (item) => (
          <div className={styles.productThumb}>
            <img
              src={isOrderPositionService(item) ? serviceIcon : item.image[0]?.thumb}
              alt={item.name}
            />
          </div>
        ),
        width: 78
      },
      {
        title: <Trans>Nazwa produktu</Trans>,
        key: 'name',
        align: 'left',
        renderCell: (item) =>
          isOrderPositionService(item) ? (
            <span>{item.name}</span>
          ) : (
            <Link to={item.url_link ? `/${item.url_link}` : '#'} className={styles.link}>
              {item.name}
            </Link>
          )
      },
      {
        title: <Trans>Ilość</Trans>,
        key: 'quantity',
        align: 'center',
        renderCell: (item) => {
          if (isOrderPositionService(item)) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontWeight: 700 }}>{item.quantity}</div>
              </div>
            );
          }
          const unit = item.units.find((unit) => unit.unit_id === item.unit_id);
          const mainUnit = item.units.find((unit) => unit.converter === 1) || item.units[0];
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontWeight: 700 }}>{item.quantity}</div>
              {unit && unit.converter_message && (
                <div style={{ fontSize: '12px', color: '#717378', marginTop: '4px' }}>
                  {unit.name} ({unit.converter_message})
                </div>
              )}
            </div>
          );
        }
      },
      {
        title: <Trans>Jednostka</Trans>,
        key: 'unit',
        align: 'center',
        renderCell: (item) => {
          if (isOrderPositionService(item)) {
            return null;
          }
          const unit = item.units.find((unit) => unit.unit_id === item.unit_id);
          const mainUnit = item.units.find((unit) => unit.converter === 1) || item.units[0];
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontWeight: 700 }}>{item.quantity} x {unit?.name}</div>
              {unit && unit.converter_message && (
                <div style={{ fontSize: '12px', color: '#717378', marginTop: '4px' }}>
                  ({unit.converter_message})
                </div>
              )}
            </div>
          );
        }
      },
      {
        title: (
          <div className={styles.noWrapLabel}>
            <Trans>Cena netto</Trans>
          </div>
        ),
        key: 'net_value',
        align: 'right',
        renderCell: (item) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div>{item.price_net_formatted} {item.currency}</div>
            {item.unit_price_net_formatted && (
              <div style={{ fontSize: '12px', color: '#717378', marginTop: '4px' }}>
                {item.unit_price_net_formatted} / szt
              </div>
            )}
          </div>
        )
      },
      {
        title: (
          <div className={styles.noWrapLabel}>
            <Trans>Cena brutto</Trans>
          </div>
        ),
        key: 'gross_value',
        align: 'right',
        renderCell: (item) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div>{item.price_gross_formatted} {item.currency}</div>
            {item.unit_price_gross_formatted && (
              <div style={{ fontSize: '12px', color: '#717378', marginTop: '4px' }}>
                {item.unit_price_gross_formatted} / szt
              </div>
            )}
          </div>
        )
      },
      {
        title: (
          <div className={styles.noWrapLabel}>
            <Trans>Wartość</Trans>
          </div>
        ),
        key: 'summary',
        align: 'right',
        renderCell: (item) => (
          <>
            <div className={classnames(styles.noWrapLabel, styles.right, styles.last)}>
              <strong>
                {item.total_price_gross_formatted} {item.currency}
              </strong>{' '}
              <span>
                <Trans>brutto</Trans>
              </span>
            </div>
            <div className={classnames(styles.noWrapLabel, styles.right, styles.last)}>
              {item.total_price_net_formatted} {item.currency}{' '}
              <span>
                <Trans>netto</Trans>
              </span>
            </div>
          </>
        )
      },
      {
        title: <Trans>Exportuj do excela</Trans>,
        key: 'export',
        align: 'center',
        renderCell: (item) => (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Button 
              onClick={() => downloadExcel()} 
              ghost 
              style={{ padding: '4px 8px', minWidth: 'auto', border: 'none', background: 'transparent' }}
            >
              <FileExportIcon style={{ color: 'var(--Primary-Blue-500, #1985C4)', fill: 'var(--Primary-Blue-500, #1985C4)' }} />
            </Button>
          </div>
        ),
        width: 50
      }
    ],
    [checkedItemIds, setCheckedItemIds, orderPositionsData, downloadExcel]
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Order-components-OrderPositions'
      )}>
      {!isMobile && (
        <div className={styles.actionsBar}>
          <ArrowDropdown />
          <div className={styles.cartSelectorWrapper}>
            <AddPositionsToCartSelect
              positions={
                orderPositionsData?.items
                  .filter(
                    (item) =>
                      checkedItemIds.includes(item.id) && !isOrderPositionService(item)
                  )
                  .map((item) => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit_id: item.unit_id
                  })) || []
              }
              onSuccess={() => setCheckedItemIds([])}
            />
          </div>

          <div className={styles.exportWrapper}>
            <Button onClick={() => downloadExcel()} ghost>
              <FileExportIcon />
              <Trans>Exportuj do Excela</Trans>
            </Button>
          </div>
        </div>
      )}
      <div className={styles.tableWrapper}>
        <Table<IOrderPositionListItem>
          columns={columns}
          mobileItem={(item) => {
            if (isOrderPositionService(item)) {
              return (
                <ProductMobile
                  item={{
                    ...item,
                    image: [{ thumb: serviceIcon, min: serviceIcon, big: serviceIcon } as any],
                    units: [{ id: 0, unit_id: 0, name: '-', converter: 1 } as any]
                  }}
                />
              );
            }
            return <ProductMobile item={item} />;
          }}
          dataSource={orderPositionsData?.items || []}
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default OrderPositions;
